import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../../shared/contexts/AuthContext';

interface Alert {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
}

export const SignUp: React.FC = () => {
    const navigate = useNavigate();
    const { signUp } = useAuth();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [alert, setAlert] = useState<Alert>({ type: 'success', message: '' });
    const [showAlert, setShowAlert] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string }>({});

    const validate = (): boolean => {
        const newErrors: { name?: string; email?: string; password?: string } = {};
        
        if (!name || name.trim() === '') {
            newErrors.name = 'Name is required';
        }
        
        if (!email) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setShowAlert(false);

        try {
            await signUp({ name, email, password });
            
            setAlert({
                type: 'success',
                message: 'Account created successfully! Redirecting to dashboard...'
            });
            setShowAlert(true);

            // Navigate to dashboard after successful sign up
            setTimeout(() => {
                navigate('/dashboard');
            }, 1000);
        } catch (error: any) {
            setIsLoading(false);
            
            // Reset form
            setPassword('');

            // Extract error message
            let errorMessage = 'Something went wrong. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlert({
                type: 'error',
                message: errorMessage
            });
            setShowAlert(true);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#e6f7f0] to-[#ffe5d4] relative overflow-hidden flex flex-col items-center justify-center min-w-0 px-4 py-8 before:content-[''] before:absolute before:inset-0 before:opacity-10 before:bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(255,255,255,0.1)_10px,rgba(255,255,255,0.1)_20px)] before:pointer-events-none">
            <div className="bg-gradient-to-br from-[#5a7ba3] to-[#4a6b8f] rounded-3xl shadow-[0_12px_48px_rgba(74,107,143,0.25),0_4px_16px_rgba(0,0,0,0.1)] relative z-10 backdrop-blur-[10px] border border-white/10 w-full max-w-md px-6 py-8 sm:px-10 sm:py-12">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                    <img 
                        src="/assets/images/logo/KOKI-logo.svg" 
                        alt="KOKI Logo"
                        className="h-12 sm:h-16"
                    />
                </div>

                {/* Welcome Text */}
                <div className="text-center mb-2">
                    <h1 className="text-white text-4xl font-bold leading-tight">Create Account</h1>
                    <p className="text-[#f0f0f0] text-sm mt-2">Enter your information on field below</p>
                </div>

                {/* Alert */}
                {showAlert && (
                    <div className={`mt-6 -mb-4 p-4 rounded-lg ${
                        alert.type === 'error' ? 'bg-red-100 text-red-700' : 
                        alert.type === 'success' ? 'bg-green-100 text-green-700' : 
                        'bg-blue-100 text-blue-700'
                    }`}>
                        {alert.message}
                    </div>
                )}

                {/* Sign up form */}
                <form className="mt-8" onSubmit={handleSubmit}>
                    {/* Name field */}
                    <TextField
                        className="w-full mb-4"
                        label="Name"
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (errors.name) setErrors({ ...errors, name: undefined });
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={isLoading}
                        fullWidth
                        required
                        sx={{
                            '& .MuiInputLabel-root': { color: '#ffffff' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '12px',
                                '& fieldset': { borderColor: 'transparent' },
                                '&:hover fieldset': { borderColor: 'transparent' },
                                '&.Mui-focused fieldset': { borderColor: 'transparent' },
                            },
                            '& .MuiInputBase-input': { color: '#1f2937' },
                        }}
                    />

                    {/* Email field */}
                    <TextField
                        className="w-full mb-4"
                        label="Email address"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isLoading}
                        fullWidth
                        required
                        sx={{
                            '& .MuiInputLabel-root': { color: '#ffffff' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '12px',
                                '& fieldset': { borderColor: 'transparent' },
                                '&:hover fieldset': { borderColor: 'transparent' },
                                '&.Mui-focused fieldset': { borderColor: 'transparent' },
                            },
                            '& .MuiInputBase-input': { color: '#1f2937' },
                        }}
                    />

                    {/* Password field */}
                    <TextField
                        className="w-full mb-2"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            if (errors.password) setErrors({ ...errors, password: undefined });
                        }}
                        error={!!errors.password}
                        helperText={errors.password || ' '}
                        disabled={isLoading}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiInputLabel-root': { color: '#ffffff' },
                            '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '12px',
                                '& fieldset': { borderColor: 'transparent' },
                                '&:hover fieldset': { borderColor: 'transparent' },
                                '&.Mui-focused fieldset': { borderColor: 'transparent' },
                            },
                            '& .MuiInputBase-input': { color: '#1f2937' },
                        }}
                    />

                    {/* Submit button */}
                    <AppButton
                        type="submit"
                        variant="primary"
                        fullWidth
                        loading={isLoading}
                        sx={{ mt: 3, py: 1.5, borderRadius: '12px' }}
                    >
                        Sign Up
                    </AppButton>
                </form>

                {/* Sign In Link */}
                <div className="mt-6 text-center text-sm text-[#f0f0f0]">
                    Already have an account? 
                    <Link to="/sign-in" className="text-[#fb923c] font-semibold no-underline ml-1 hover:text-[#f97316] hover:underline transition-colors duration-200">Sign In</Link>
                </div>
            </div>
        </div>
    );
};
